/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { InputAction } from "../enums/InputAction";
import type { Platform } from "./types/TPlatform";

interface KeyMapping {
  byKey: Record<string, InputAction>;
  byKeyCode: Record<number, InputAction>;
}

const BROWSER_MAP: KeyMapping = {
  byKey: {
    ArrowUp: InputAction.NAV_UP,
    ArrowDown: InputAction.NAV_DOWN,
    ArrowLeft: InputAction.NAV_LEFT,
    ArrowRight: InputAction.NAV_RIGHT,
    Enter: InputAction.NAV_OK,
    Escape: InputAction.BACK,
    Backspace: InputAction.BACK,
    x: InputAction.EXIT,
    X: InputAction.EXIT,
    i: InputAction.INFO,
    I: InputAction.INFO,
    "/": InputAction.INFO,
    g: InputAction.GUIDE,
    G: InputAction.GUIDE,
    d: InputAction.COLOR_BLUE,
    D: InputAction.COLOR_BLUE,
    ";": InputAction.COLOR_RED,
    f: InputAction.FAST_FORWARD,
    F: InputAction.FAST_FORWARD,
    r: InputAction.REWIND,
    R: InputAction.REWIND,
    s: InputAction.STOP,
    S: InputAction.STOP,
    o: InputAction.PLAY_PAUSE,
    O: InputAction.PLAY_PAUSE,
    p: InputAction.PLAY_PAUSE,
    P: InputAction.PLAY_PAUSE,
    " ": InputAction.PLAY_PAUSE,
    ".": InputAction.CHANNEL_UP,
    ",": InputAction.CHANNEL_DOWN,
    "0": InputAction.NUM_0,
    "1": InputAction.NUM_1,
    "2": InputAction.NUM_2,
    "3": InputAction.NUM_3,
    "4": InputAction.NUM_4,
    "5": InputAction.NUM_5,
    "6": InputAction.NUM_6,
    "7": InputAction.NUM_7,
    "8": InputAction.NUM_8,
    "9": InputAction.NUM_9
  },
  byKeyCode: {
    38: InputAction.NAV_UP,
    40: InputAction.NAV_DOWN,
    37: InputAction.NAV_LEFT,
    39: InputAction.NAV_RIGHT,
    13: InputAction.NAV_OK,
    27: InputAction.BACK,
    73: InputAction.INFO,
    191: InputAction.INFO,
    8: InputAction.BACK,
    190: InputAction.CHANNEL_UP,
    188: InputAction.CHANNEL_DOWN,
    48: InputAction.NUM_0,
    49: InputAction.NUM_1,
    50: InputAction.NUM_2,
    51: InputAction.NUM_3,
    52: InputAction.NUM_4,
    53: InputAction.NUM_5,
    54: InputAction.NUM_6,
    55: InputAction.NUM_7,
    56: InputAction.NUM_8,
    57: InputAction.NUM_9
  }
};

const TIZEN_MAP: KeyMapping = {
  byKey: {
    ArrowUp: InputAction.NAV_UP,
    ArrowDown: InputAction.NAV_DOWN,
    ArrowLeft: InputAction.NAV_LEFT,
    ArrowRight: InputAction.NAV_RIGHT,
    Enter: InputAction.NAV_OK,
    xf86back: InputAction.BACK,
    XF86Back: InputAction.BACK,
    xf86exit: InputAction.EXIT,
    XF86Exit: InputAction.EXIT,
    xf86info: InputAction.INFO,
    XF86Info: InputAction.INFO,
    xf86channelguide: InputAction.GUIDE,
    XF86ChannelGuide: InputAction.GUIDE,
    xf86red: InputAction.COLOR_RED,
    XF86Red: InputAction.COLOR_RED,
    xf86blue: InputAction.COLOR_BLUE,
    XF86Blue: InputAction.COLOR_BLUE,
    xf86raisechannel: InputAction.CHANNEL_UP,
    xf86lowerchannel: InputAction.CHANNEL_DOWN,
    XF86RaiseChannel: InputAction.CHANNEL_UP,
    XF86LowerChannel: InputAction.CHANNEL_DOWN,
    ChannelUp: InputAction.CHANNEL_UP,
    ChannelDown: InputAction.CHANNEL_DOWN,
    PageUp: InputAction.CHANNEL_UP,
    PageDown: InputAction.CHANNEL_DOWN,
    xf86playback: InputAction.PLAY_PAUSE,
    xf86audioplay: InputAction.PLAY_PAUSE,
    xf86audiopause: InputAction.PLAY_PAUSE,
    xf86audiorewind: InputAction.REWIND,
    xf86audionext: InputAction.FAST_FORWARD,
    xf86audiostop: InputAction.STOP,
    "0": InputAction.NUM_0,
    "1": InputAction.NUM_1,
    "2": InputAction.NUM_2,
    "3": InputAction.NUM_3,
    "4": InputAction.NUM_4,
    "5": InputAction.NUM_5,
    "6": InputAction.NUM_6,
    "7": InputAction.NUM_7,
    "8": InputAction.NUM_8,
    "9": InputAction.NUM_9
  },
  byKeyCode: {
    38: InputAction.NAV_UP,
    40: InputAction.NAV_DOWN,
    37: InputAction.NAV_LEFT,
    39: InputAction.NAV_RIGHT,
    13: InputAction.NAV_OK,
    10009: InputAction.BACK,
    10182: InputAction.EXIT,
    10252: InputAction.PLAY_PAUSE,
    403: InputAction.COLOR_RED,
    404: InputAction.COLOR_GREEN,
    405: InputAction.COLOR_YELLOW,
    406: InputAction.COLOR_BLUE,
    427: InputAction.CHANNEL_UP,
    428: InputAction.CHANNEL_DOWN,
    33: InputAction.CHANNEL_UP,
    34: InputAction.CHANNEL_DOWN,
    412: InputAction.REWIND,
    417: InputAction.FAST_FORWARD,
    413: InputAction.STOP,
    48: InputAction.NUM_0,
    49: InputAction.NUM_1,
    50: InputAction.NUM_2,
    51: InputAction.NUM_3,
    52: InputAction.NUM_4,
    53: InputAction.NUM_5,
    54: InputAction.NUM_6,
    55: InputAction.NUM_7,
    56: InputAction.NUM_8,
    57: InputAction.NUM_9
  }
};

const WEBOS_MAP: KeyMapping = {
  byKey: {
    ArrowUp: InputAction.NAV_UP,
    ArrowDown: InputAction.NAV_DOWN,
    ArrowLeft: InputAction.NAV_LEFT,
    ArrowRight: InputAction.NAV_RIGHT,
    Enter: InputAction.NAV_OK,
    PageUp: InputAction.CHANNEL_UP,
    pageup: InputAction.CHANNEL_UP,
    PageDown: InputAction.CHANNEL_DOWN,
    pagedown: InputAction.CHANNEL_DOWN,
    ChannelUp: InputAction.CHANNEL_UP,
    ChannelDown: InputAction.CHANNEL_DOWN,
    "0": InputAction.NUM_0,
    "1": InputAction.NUM_1,
    "2": InputAction.NUM_2,
    "3": InputAction.NUM_3,
    "4": InputAction.NUM_4,
    "5": InputAction.NUM_5,
    "6": InputAction.NUM_6,
    "7": InputAction.NUM_7,
    "8": InputAction.NUM_8,
    "9": InputAction.NUM_9
  },
  byKeyCode: {
    38: InputAction.NAV_UP,
    40: InputAction.NAV_DOWN,
    37: InputAction.NAV_LEFT,
    39: InputAction.NAV_RIGHT,
    13: InputAction.NAV_OK,
    461: InputAction.BACK,
    33: InputAction.CHANNEL_UP,
    34: InputAction.CHANNEL_DOWN,
    427: InputAction.CHANNEL_UP,
    428: InputAction.CHANNEL_DOWN,
    403: InputAction.COLOR_RED,
    404: InputAction.COLOR_GREEN,
    405: InputAction.COLOR_YELLOW,
    406: InputAction.COLOR_BLUE,
    415: InputAction.PLAY_PAUSE,
    19: InputAction.PLAY_PAUSE,
    412: InputAction.REWIND,
    417: InputAction.FAST_FORWARD,
    413: InputAction.STOP,
    48: InputAction.NUM_0,
    49: InputAction.NUM_1,
    50: InputAction.NUM_2,
    51: InputAction.NUM_3,
    52: InputAction.NUM_4,
    53: InputAction.NUM_5,
    54: InputAction.NUM_6,
    55: InputAction.NUM_7,
    56: InputAction.NUM_8,
    57: InputAction.NUM_9
  }
};

const VEGAOS_MAP: KeyMapping = {
  byKey: {
    ArrowUp: InputAction.NAV_UP,
    ArrowDown: InputAction.NAV_DOWN,
    ArrowLeft: InputAction.NAV_LEFT,
    ArrowRight: InputAction.NAV_RIGHT,
    Enter: InputAction.NAV_OK,
    GoBack: InputAction.BACK,
    goback: InputAction.BACK,
    MediaPlayPause: InputAction.PLAY_PAUSE,
    mediaplaypause: InputAction.PLAY_PAUSE,
    MediaRewind: InputAction.REWIND,
    mediarewind: InputAction.REWIND,
    MediaFastForward: InputAction.FAST_FORWARD,
    mediafastforward: InputAction.FAST_FORWARD,
    MediaStop: InputAction.STOP,
    "0": InputAction.NUM_0,
    "1": InputAction.NUM_1,
    "2": InputAction.NUM_2,
    "3": InputAction.NUM_3,
    "4": InputAction.NUM_4,
    "5": InputAction.NUM_5,
    "6": InputAction.NUM_6,
    "7": InputAction.NUM_7,
    "8": InputAction.NUM_8,
    "9": InputAction.NUM_9
  },
  byKeyCode: {
    38: InputAction.NAV_UP,
    40: InputAction.NAV_DOWN,
    37: InputAction.NAV_LEFT,
    39: InputAction.NAV_RIGHT,
    13: InputAction.NAV_OK,
    27: InputAction.BACK,
    179: InputAction.PLAY_PAUSE,
    227: InputAction.REWIND,
    228: InputAction.FAST_FORWARD,
    178: InputAction.STOP,
    48: InputAction.NUM_0,
    49: InputAction.NUM_1,
    50: InputAction.NUM_2,
    51: InputAction.NUM_3,
    52: InputAction.NUM_4,
    53: InputAction.NUM_5,
    54: InputAction.NUM_6,
    55: InputAction.NUM_7,
    56: InputAction.NUM_8,
    57: InputAction.NUM_9
  }
};

const PLATFORM_MAPS: Record<Platform, KeyMapping> = {
  browser: BROWSER_MAP,
  tizen: TIZEN_MAP,
  webos: WEBOS_MAP,
  vegaos: VEGAOS_MAP
};

/**
 * Translates raw KeyboardEvent → abstract InputAction.
 * Priority: custom overrides → event.key → event.keyCode → UNKNOWN.
 */
export class PlatformKeyMapper {
  private mMapping: KeyMapping;
  private mCustomOverrides: KeyMapping = { byKey: {}, byKeyCode: {} };

  constructor(platform: Platform) {
    this.mMapping = PLATFORM_MAPS[platform];
  }

  public addMapping(keyOrCode: string | number, action: InputAction): void {
    if (typeof keyOrCode === "string") {
      this.mCustomOverrides.byKey[keyOrCode] = action;
    } else {
      this.mCustomOverrides.byKeyCode[keyOrCode] = action;
    }
  }

  public resolve(event: KeyboardEvent): InputAction {
    if (event.key && this.mCustomOverrides.byKey[event.key]) {
      return this.mCustomOverrides.byKey[event.key];
    }
    if (this.mCustomOverrides.byKeyCode[event.keyCode]) {
      return this.mCustomOverrides.byKeyCode[event.keyCode];
    }
    if (event.key && this.mMapping.byKey[event.key]) {
      return this.mMapping.byKey[event.key];
    }
    if (this.mMapping.byKeyCode[event.keyCode]) {
      return this.mMapping.byKeyCode[event.keyCode];
    }
    return InputAction.UNKNOWN;
  }
}
